import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { WorkOrderDetailStatus } from '../types';

const PROGRESS_CONSTANTS = {
  HEIGHT: 8,
  BORDER_RADIUS: 4,
  CHIP_HEIGHT: 24,
  CHIP_FONT_SIZE: '0.75rem',
  ICON_SIZE: 20,
  CHIP_PADDING: 8
};

const PROGRESS_THRESHOLDS = {
  EXCELLENT: 75,
  GOOD: 50,
  FAIR: 25
};

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: PROGRESS_CONSTANTS.HEIGHT,
  borderRadius: PROGRESS_CONSTANTS.BORDER_RADIUS,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  '& .MuiLinearProgress-bar': {
    borderRadius: PROGRESS_CONSTANTS.BORDER_RADIUS,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    transition: 'transform 0.4s ease'
  }
}));

const ProgressChip = styled(Chip)(() => ({
  fontWeight: 600,
  fontSize: PROGRESS_CONSTANTS.CHIP_FONT_SIZE,
  height: PROGRESS_CONSTANTS.CHIP_HEIGHT,
  '& .MuiChip-label': {
    paddingLeft: PROGRESS_CONSTANTS.CHIP_PADDING,
    paddingRight: PROGRESS_CONSTANTS.CHIP_PADDING
  }
}));

const StepIcon = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1
}));

const StepConnector = styled(Box, {
  shouldForwardProp: prop => prop !== 'isCompleted'
})<{ isCompleted: boolean }>(({ theme, isCompleted }) => ({
  flex: 1,
  height: 2,
  backgroundColor: isCompleted
    ? theme.palette.success.main
    : theme.palette.divider,
  mx: 0.5,
  transition: 'background-color 0.3s ease'
}));

const CompletionText = styled(Typography)(({ theme }) => ({
  color: theme.palette.success.main,
  fontWeight: 600,
  animation: 'fadeIn 0.5s ease',
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  }
}));

const StatusChip = styled(Chip)(() => ({
  fontSize: '0.7rem',
  height: 20,
  '& .MuiChip-label': {
    paddingLeft: 6,
    paddingRight: 6
  }
}));

const StatusSummary = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.5)
      : alpha(theme.palette.grey[100], 0.8),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  marginTop: theme.spacing(0.5)
}));

type StatusCounts = {
  [WorkOrderDetailStatus.COMPLETE]: number;
  [WorkOrderDetailStatus.INCOMPLETE]: number;
  [WorkOrderDetailStatus.SKIPPED]: number;
  [WorkOrderDetailStatus.BACKORDERED]: number;
};

type ProgressBarProps = {
  completed: number;
  total: number;
  showPercentage?: boolean;
  showSteps?: boolean;
  showStatusCounts?: boolean;
  variant?: 'linear' | 'circular' | 'stepped';
  statusCounts?: StatusCounts;
  itemType?: 'tasks' | 'equipment';
};

const useProgressLogic = (completed: number, total: number) => {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isComplete = completed === total && total > 0;

  const getProgressColor = () => {
    if (isComplete) return 'success';
    if (percentage >= PROGRESS_THRESHOLDS.EXCELLENT) return 'primary';
    if (percentage >= PROGRESS_THRESHOLDS.GOOD) return 'info';
    if (percentage >= PROGRESS_THRESHOLDS.FAIR) return 'warning';
    return 'inherit';
  };

  const getChipColor = () => {
    if (isComplete) return 'success';
    if (percentage >= PROGRESS_THRESHOLDS.EXCELLENT) return 'primary';
    if (percentage >= PROGRESS_THRESHOLDS.GOOD) return 'info';
    return 'default';
  };

  return { percentage, isComplete, getProgressColor, getChipColor };
};

export const ProgressBar: React.FC<ProgressBarProps> = ({
  completed,
  total,
  showPercentage = true,
  showSteps = true,
  showStatusCounts = false,
  variant = 'linear',
  statusCounts,
  itemType = 'tasks'
}) => {
  const { t } = useTranslation('workOrders');
  const { percentage, isComplete, getProgressColor, getChipColor } =
    useProgressLogic(completed, total);

  const getStatusColors = (status: WorkOrderDetailStatus) => {
    switch (status) {
      case WorkOrderDetailStatus.COMPLETE:
        return {
          backgroundColor: 'rgba(76, 175, 80, 0.15)',
          color: '#4caf50',
          border: '1px solid rgba(76, 175, 80, 0.4)'
        };
      case WorkOrderDetailStatus.INCOMPLETE:
        return {
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          color: '#ff9800',
          border: '1px solid rgba(255, 152, 0, 0.4)'
        };
      case WorkOrderDetailStatus.SKIPPED:
        return {
          backgroundColor: 'rgba(158, 158, 158, 0.15)',
          color: '#9e9e9e',
          border: '1px solid rgba(158, 158, 158, 0.4)'
        };
      case WorkOrderDetailStatus.BACKORDERED:
        return {
          backgroundColor: 'rgba(156, 39, 176, 0.15)',
          color: '#ba68c8',
          border: '1px solid rgba(156, 39, 176, 0.4)'
        };
      default:
        return {
          backgroundColor: 'rgba(158, 158, 158, 0.15)',
          color: '#9e9e9e',
          border: '1px solid rgba(158, 158, 158, 0.4)'
        };
    }
  };

  const getProgressText = () => {
    const key =
      itemType === 'equipment' ? 'equipmentComplete' : 'tasksComplete';
    return t(`workOrder.progress.${key}`, { completed, total });
  };

  const renderStatusSummary = () => {
    if (!showStatusCounts || !statusCounts) return null;

    const statusEntries = Object.entries(statusCounts).filter(
      ([_, count]) => count > 0
    );
    if (statusEntries.length === 0) return null;

    return (
      <StatusSummary>
        <Stack spacing={1}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            {t('workOrder.progress.summary.breakdown')}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {statusEntries.map(([status, count]) => {
              const statusKey = status.toLowerCase();
              return (
                <StatusChip
                  key={status}
                  label={t(`workOrder.progress.statusCounts.${statusKey}`, {
                    count
                  })}
                  size="small"
                  sx={getStatusColors(status as WorkOrderDetailStatus)}
                />
              );
            })}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t('workOrder.progress.summary.totalItems', { total })}
          </Typography>
        </Stack>
      </StatusSummary>
    );
  };

  const renderStepIcon = (index: number, isCompleted: boolean) => (
    <Tooltip
      key={index}
      title={
        t('workOrder.progress.taskNumber', { number: index + 1 }) +
        ': ' +
        t(
          isCompleted
            ? 'workOrder.progress.taskComplete'
            : 'workOrder.progress.taskPending'
        )
      }
      arrow
      placement="top"
    >
      <StepIcon>
        {isCompleted ? (
          <CheckCircleIcon
            sx={{
              fontSize: PROGRESS_CONSTANTS.ICON_SIZE,
              color: 'success.main',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.7 },
                '100%': { opacity: 1 }
              }
            }}
          />
        ) : (
          <RadioButtonUncheckedIcon
            sx={{
              fontSize: PROGRESS_CONSTANTS.ICON_SIZE,
              color: 'text.disabled'
            }}
          />
        )}
        {index < total - 1 && <StepConnector isCompleted={isCompleted} />}
      </StepIcon>
    </Tooltip>
  );

  const renderSteppedProgress = () => (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
        {[...Array(total)].map((_, index) =>
          renderStepIcon(index, index < completed)
        )}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {getProgressText()}
        </Typography>
      </Stack>
      {renderStatusSummary()}
    </Box>
  );

  if (variant === 'stepped') {
    return renderSteppedProgress();
  }

  const renderLinearProgress = () => (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <StyledLinearProgress
            variant="determinate"
            value={percentage}
            color={getProgressColor()}
          />
        </Box>
        {showPercentage && (
          <ProgressChip
            label={`${percentage}%`}
            size="small"
            color={getChipColor()}
            variant={isComplete ? 'filled' : 'outlined'}
          />
        )}
      </Stack>
      {showSteps && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="caption" color="text.secondary">
            {getProgressText()}
          </Typography>
          {isComplete && (
            <CompletionText variant="caption">
              ✓ {t('workOrder.progress.allTasksComplete')}
            </CompletionText>
          )}
        </Stack>
      )}
      {renderStatusSummary()}
    </Box>
  );

  return renderLinearProgress();
};
